import { useState, useEffect, useRef } from 'react';
import { animate, scrambleText } from 'animejs';
import { useAppStore } from '../store';
import Btn from '../ui/Btn';
import Panel from '../ui/Panel';
import Crosshair from '../brand/Crosshair';
import SectionTitle from '../ui/SectionTitle';

const ITSHECHKA_IG = 'https://www.instagram.com/itshechkaofficial?igsh=MXJiazNsenVmamdvNw%3D%3D&utm_source=qr';

/* ── scroll-reveal ─────────────────────────────────── */
function useInView(threshold = 0.18) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSeen(true); io.disconnect(); }
    }, { threshold, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function Reveal({ children, delay = 0, y = 22, style, className }) {
  const [ref, seen] = useInView(0.14);
  return (
    <div ref={ref} className={className} style={{
      opacity: seen ? 1 : 0,
      transform: seen ? 'translateY(0)' : `translateY(${y}px)`,
      transition: `opacity .7s var(--ease-out) ${delay}ms, transform .7s var(--ease-out) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ── count-up ──────────────────────────────────────── */
function StatNum({ num, suffix = '', inf, seen, dur = 1500 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!seen || inf) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setV(num); return; }
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setV(Math.round(e * num));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [seen, num, dur, inf]);
  return <>{inf ? '∞' : v}{!inf && suffix}</>;
}

/* ── data ──────────────────────────────────────────── */
const TEAM_STATS = [
  { num: 2,  suffix: '+', label: 'года\nв разработке' },
  { num: 10, suffix: '+', label: 'успешных\nпроектов' },
  { num: 3,  suffix: '',  label: 'специалиста\nв команде' },
  { inf: true,            label: 'идей\nвпереди' },
];

const TEAM_SERVICES = [
  { title: 'Автоматизация систем',
    desc: 'Строим автоматизированные пайплайны для бизнес-процессов, CRM и внутренней инфраструктуры.',
    icon: 'M4 7h16M4 12h10M4 17h7' },
  { title: 'ИИ и нейросети',
    desc: 'Интеграция LLM, обучение собственных моделей и AI-решения под задачи заказчика.',
    icon: 'M12 3v3M12 18v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M3 12h3M18 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1' },
  { title: 'Веб-разработка',
    desc: 'Современные сайты и веб-приложения — от лендингов до полноценных платформ с бэкендом.',
    icon: 'M3 5h18v14H3zM3 9h18' },
  { title: 'API-интеграции',
    desc: 'Подключаем платёжные системы, мессенджеры, внешние API и сервисы в единую экосистему.',
    icon: 'M9 7l-4 5 4 5M15 7l4 5-4 5' },
];

const TEAM_MEMBERS = [
  { name: 'Артем Пинигин',  role: 'Frontend разработчик', tag: 'FRONTEND', captain: true,  initials: 'АП', accent: 'var(--gold)' },
  { name: 'Жумабек Алихан', role: 'Frontend разработчик', tag: 'FRONTEND', captain: false, initials: 'ЖА', accent: 'var(--accent)' },
  { name: 'Ефремов Иван',   role: 'Backend разработчик',  tag: 'BACKEND',  captain: false, initials: 'ЕИ', accent: 'var(--info)' },
];

const TECH_STACK = ['React', 'Node.js', 'Python', 'LLM / AI', 'PostgreSQL', 'REST API', 'Automation', 'TypeScript'];

const TERM_LINES = [
  { p: '$', t: 'itshechka deploy', cls: 'cmd' },
  { p: '>', t: 'building site ........ ok', cls: 'ok' },
  { p: '>', t: 'optimizing assets .... ok', cls: 'ok' },
  { p: '>', t: 'AI pipeline .......... on', cls: 'ok' },
  { p: '>', t: 'api integrations ..... ok', cls: 'ok' },
  { p: '✓', t: 'status: DEPLOYED', cls: 'done' },
];

/* ── terminal ──────────────────────────────────────── */
function DevTerminal() {
  const [ref, seen] = useInView(0.3);
  return (
    <div ref={ref} className="dv-term-wrap" style={{ width: 'min(440px, 90vw)' }}>
      <div className={`dv-term clip-both ${seen ? 'play' : ''}`}>
        <div className="dv-term-bar">
          <span className="dv-dot" style={{ background: 'var(--accent)' }} />
          <span className="dv-dot" style={{ background: 'var(--gold)' }} />
          <span className="dv-dot" style={{ background: 'var(--fg-faint)' }} />
          <span className="mono dv-term-title">itshechka@build — zsh</span>
        </div>
        <div className="dv-term-body mono">
          {TERM_LINES.map((l, i) => (
            <div key={i} className="dv-line" style={{ animationDelay: `${0.25 + i * 0.42}s` }}>
              <span className={`dv-pr dv-pr-${l.cls}`}>{l.p}</span>
              <span className={`dv-txt dv-txt-${l.cls}`}>{l.t}</span>
            </div>
          ))}
          <div className="dv-line dv-caret-line" style={{ animationDelay: `${0.25 + TERM_LINES.length * 0.42}s` }}>
            <span className="dv-pr dv-pr-cmd">$</span>
            <span className="dv-caret" />
          </div>
        </div>
      </div>
      <div aria-hidden className="dv-term-glow" />
    </div>
  );
}

/* ── service card ──────────────────────────────────── */
function DevServiceCard({ title, desc, icon, index }) {
  return (
    <div className="dv-svc clip-tr">
      <span aria-hidden className="dv-scan" />
      <div className="display dv-svc-ghost">0{index + 1}</div>
      <div className="dv-svc-ico">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d={icon} />
        </svg>
      </div>
      <h3 className="display dv-svc-title">{title}</h3>
      <p className="dv-svc-desc">{desc}</p>
      <div className="dv-svc-rule" />
    </div>
  );
}

/* ── member card ───────────────────────────────────── */
function DevMemberCard({ m }) {
  return (
    <div className="dv-mem clip-both" style={{ '--mc': m.accent }}>
      <span aria-hidden className="dv-scan" />
      <span aria-hidden className="dv-mem-strip" />
      {m.captain && (
        <span className="mono dv-mem-cap">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.3 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6z" /></svg>
          КАПИТАН · IGL
        </span>
      )}
      <div className="dv-ava-wrap">
        <svg viewBox="0 0 100 100" className="dv-ava-ring" aria-hidden>
          <circle cx="50" cy="50" r="47" fill="none" stroke="var(--mc)" strokeWidth="1" strokeDasharray="3 6" opacity="0.7" />
        </svg>
        <div className="dv-ava">{m.initials}</div>
      </div>
      <div className="mono dv-mem-tag">{m.tag}</div>
      <h3 className="display dv-mem-name">{m.name}</h3>
      <span className="dv-mem-role">{m.role}</span>
    </div>
  );
}

/* ── IG icon ───────────────────────────────────────── */
function IgIcon({ size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85 0 3.2-.01 3.58-.07 4.85-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07-3.2 0-3.58-.01-4.85-.07-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.65-.07-4.85 0-3.2.01-3.58.07-4.85.15-3.23 1.66-4.77 4.92-4.92C8.42 2.17 8.8 2.16 12 2.16zm0-2.16C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.2-4.35-2.62-6.78-6.98-6.98C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44z" />
    </svg>
  );
}

/* ── scoped styles ─────────────────────────────────── */
function DevStyles() {
  return (
    <style>{`
    @keyframes goldShimmer { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
    @keyframes dvLineIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
    @keyframes dvMarq { from{transform:translateX(0)} to{transform:translateX(-50%)} }

    .dv-back { display:inline-flex;align-items:center;gap:7px;background:none;border:none;cursor:pointer;
      color:var(--fg-faint);font-size:11px;letter-spacing:0.16em;padding:6px 0;transition:color .18s,gap .18s;font-family:var(--font-mono);white-space:nowrap; }
    .dv-back:hover { color:var(--fg-soft);gap:11px; }

    .dv-hero { display:grid;grid-template-columns:minmax(0,1.12fr) minmax(0,0.88fr);gap:52px;align-items:center; }
    .dv-hero-right { display:flex;justify-content:center;min-width:0; }
    .dv-h1 { font-size:clamp(46px,8.5vw,104px);line-height:0.9;letter-spacing:-0.01em; }
    .dv-h1-shimmer {
      background:linear-gradient(100deg,#fff 0%,#fff 30%,var(--gold) 55%,#fff 80%);
      background-size:220% auto;
      -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent;
      animation:goldShimmer 5s linear infinite;
    }
    .dv-h1-stroke { -webkit-text-stroke:1.5px var(--accent);color:transparent;display:inline-block;margin-top:4px; }
    .dv-lead { margin:0;font-size:clamp(15px,1.9vw,18px);color:var(--fg-soft);max-width:540px;line-height:1.62; }
    .dv-lead strong { color:var(--fg);font-weight:700; }

    .dv-stack { display:flex;flex-wrap:wrap;gap:8px;margin-top:4px; }
    .dv-stack-chip { font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--fg-muted);white-space:nowrap;
      padding:6px 11px;border:1px solid var(--line);border-radius:var(--r-sm);background:rgba(255,255,255,0.025);
      transition:color .2s,border-color .2s,background .2s,transform .2s; }
    .dv-stack-chip:hover { color:var(--gold);border-color:rgba(230,183,60,0.4);background:rgba(230,183,60,0.06);transform:translateY(-2px); }

    .dv-term-wrap { position:relative; }
    .dv-term { position:relative;z-index:1;background:linear-gradient(165deg,#101015,#0a0a0d);
      border:1px solid var(--line-strong);box-shadow:var(--shadow-pop),0 0 40px -16px var(--accent-glow);overflow:hidden; }
    .dv-term-bar { display:flex;align-items:center;gap:7px;padding:11px 16px;border-bottom:1px solid var(--line);background:rgba(255,255,255,0.025); }
    .dv-dot { width:10px;height:10px;border-radius:50%;display:inline-block;box-shadow:0 0 8px -1px currentColor; }
    .dv-term-title { margin-left:10px;font-size:10.5px;letter-spacing:0.1em;color:var(--fg-faint); }
    .dv-term-body { padding:20px 18px 22px;font-size:12.5px;line-height:1.9;min-height:220px; }
    .dv-line { display:flex;gap:10px;align-items:baseline;opacity:0;white-space:nowrap; }
    .dv-term.play .dv-line { animation:dvLineIn .42s var(--ease-out) both;animation-delay:inherit; }
    .dv-pr { flex-shrink:0;font-weight:700;width:12px; }
    .dv-pr-cmd { color:var(--accent-bright); }
    .dv-pr-ok { color:var(--fg-faint); }
    .dv-pr-done { color:var(--ok); }
    .dv-txt-cmd { color:var(--fg); }
    .dv-txt-ok { color:var(--fg-muted); }
    .dv-txt-done { color:var(--ok);font-weight:700;letter-spacing:0.04em; }
    .dv-caret { display:inline-block;width:9px;height:15px;background:var(--accent-bright);
      box-shadow:0 0 8px var(--accent-glow);animation:c4blink 1.05s steps(1) infinite;transform:translateY(2px); }
    .dv-term-glow { position:absolute;inset:-18px;z-index:0;border-radius:24px;
      background:radial-gradient(ellipse at 60% 40%,var(--accent-glow),transparent 70%);filter:blur(34px);opacity:0.5; }

    .dv-stats { display:grid;grid-template-columns:repeat(4,1fr); }
    .dv-stat-cell { padding:30px 18px;text-align:center;border-right:1px solid var(--line); }
    .dv-stat-cell:last-child { border-right:none; }
    .dv-stat-num { font-size:clamp(38px,5vw,56px);line-height:1;letter-spacing:-0.02em; }
    .dv-stat-label { margin-top:10px;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;
      color:var(--fg-muted);line-height:1.55;white-space:pre-line; }

    .dv-marquee { display:flex;width:max-content;animation:dvMarq linear infinite; }
    .dv-marquee-inner { display:flex;align-items:center; }
    .dv-marquee-item { display:flex;align-items:center;gap:26px;padding:0 26px; }
    .dv-marquee-item .display { font-size:20px;color:var(--fg-faint);letter-spacing:0.06em; }

    .dv-svc-grid { display:grid;grid-template-columns:repeat(auto-fit,minmax(min(232px,100%),1fr));gap:18px; }
    .dv-svc { position:relative;overflow:hidden;padding:30px 26px 32px;
      background:linear-gradient(160deg,var(--surface-2),var(--surface));border:1px solid var(--line);
      transition:transform .3s var(--ease-out),box-shadow .3s,border-color .3s;cursor:default; }
    .dv-svc:hover { transform:translateY(-5px);border-color:var(--line-accent);
      box-shadow:var(--shadow-card),0 0 30px -8px var(--accent-glow); }
    .dv-svc-ghost { position:absolute;top:6px;right:14px;font-size:58px;line-height:1;
      color:rgba(225,18,23,0.12);pointer-events:none; }
    .dv-svc-ico { width:46px;height:46px;display:flex;align-items:center;justify-content:center;
      color:var(--accent-bright);background:rgba(225,18,23,0.10);border:1px solid var(--line-accent);
      border-radius:var(--r-md);margin-bottom:18px;transition:color .3s,background .3s; }
    .dv-svc:hover .dv-svc-ico { color:var(--gold);background:rgba(230,183,60,0.1);border-color:rgba(230,183,60,0.4); }
    .dv-svc-title { font-size:18px;margin:0 0 10px;text-transform:none;letter-spacing:0; }
    .dv-svc-desc { margin:0;font-size:13.5px;color:var(--fg-muted);line-height:1.6; }
    .dv-svc-rule { width:34px;height:3px;background:var(--accent);margin-top:18px;
      box-shadow:0 0 8px var(--accent-glow);transition:width .3s var(--ease-out); }
    .dv-svc:hover .dv-svc-rule { width:62px; }

    .dv-scan { position:absolute;top:0;left:0;width:40%;height:100%;pointer-events:none;z-index:2;opacity:0;
      background:linear-gradient(105deg,transparent,rgba(255,255,255,0.07),transparent); }
    .dv-svc:hover .dv-scan,.dv-mem:hover .dv-scan { opacity:1;animation:scanSweep 0.9s var(--ease-out); }

    .dv-mem-grid { display:grid;grid-template-columns:repeat(auto-fit,minmax(min(258px,100%),1fr));gap:20px; }
    .dv-mem { position:relative;overflow:hidden;text-align:center;padding:38px 26px 32px;
      background:linear-gradient(165deg,var(--surface-2),var(--surface));border:1px solid var(--line);
      transition:transform .32s var(--ease-out),box-shadow .32s,border-color .32s;cursor:default; }
    .dv-mem:hover { transform:translateY(-6px);border-color:color-mix(in srgb,var(--mc) 45%,transparent);
      box-shadow:var(--shadow-pop),0 0 36px -10px color-mix(in srgb,var(--mc) 60%,transparent); }
    .dv-mem-strip { position:absolute;top:0;left:0;right:0;height:3px;
      background:linear-gradient(90deg,transparent,var(--mc),transparent);opacity:0.85; }
    .dv-mem-cap { position:absolute;top:13px;right:14px;display:inline-flex;align-items:center;gap:5px;
      font-size:9px;font-weight:700;letter-spacing:0.08em;color:var(--gold);
      background:rgba(230,183,60,0.1);border:1px solid rgba(230,183,60,0.4);border-radius:var(--r-sm);padding:4px 9px; }
    .dv-ava-wrap { position:relative;width:96px;height:96px;margin:6px auto 20px; }
    .dv-ava-ring { position:absolute;inset:-8px;width:calc(100% + 16px);height:calc(100% + 16px);animation:spin 22s linear infinite; }
    .dv-ava { width:96px;height:96px;border-radius:50%;display:flex;align-items:center;justify-content:center;
      font-family:var(--font-display);font-weight:700;font-size:30px;color:#fff;letter-spacing:0.03em;user-select:none;
      background:radial-gradient(circle at 32% 28%,color-mix(in srgb,var(--mc) 75%,#000),color-mix(in srgb,var(--mc) 18%,#0a0a0d));
      border:1.5px solid color-mix(in srgb,var(--mc) 55%,transparent);
      box-shadow:inset 0 2px 8px rgba(255,255,255,0.18),0 0 22px -6px var(--mc);transition:box-shadow .32s; }
    .dv-mem:hover .dv-ava { box-shadow:inset 0 2px 8px rgba(255,255,255,0.22),0 0 40px -4px var(--mc); }
    .dv-mem-tag { font-size:10px;letter-spacing:0.22em;color:var(--mc);margin-bottom:9px; }
    .dv-mem-name { font-size:21px;margin:0 0 12px;text-transform:none;letter-spacing:-0.01em; }
    .dv-mem-role { display:inline-block;font-size:11px;font-family:var(--font-mono);letter-spacing:0.1em;text-transform:uppercase;
      color:var(--fg-muted);background:rgba(255,255,255,0.04);border:1px solid var(--line);border-radius:20px;padding:6px 15px; }

    .dv-cta { position:relative;overflow:hidden;text-align:center;padding:clamp(44px,7vw,76px) 24px;
      background:linear-gradient(135deg,var(--accent-deep),#2a0608 58%,var(--surface));
      border:1px solid var(--line-accent);border-radius:var(--r-xl); }
    .dv-cta-glow { position:absolute;top:-42%;left:50%;transform:translateX(-50%);width:560px;height:330px;
      background:radial-gradient(ellipse,var(--accent-glow),transparent 64%);filter:blur(34px);pointer-events:none; }
    .dv-cta .dv-cta-title { font-size:clamp(30px,5vw,54px);margin:14px 0 16px;line-height:1.1; }
    .dv-cta .dv-cta-sub { color:var(--fg-soft);max-width:480px;margin:0 auto 28px;font-size:16px;line-height:1.6; }
    .dv-cta button { width:min(100%,440px); }

    @media (max-width: 920px) {
      .dv-hero { grid-template-columns:1fr;gap:36px; }
      .dv-hero-right { order:-1; }
      .dv-term-wrap { width:min(560px,100%) !important; }
      .dv-stats { grid-template-columns:repeat(2,1fr); }
      .dv-stat-cell:nth-child(2) { border-right:none; }
      .dv-stat-cell:nth-child(1),.dv-stat-cell:nth-child(2) { border-bottom:1px solid var(--line); }
    }
    @media (max-width: 480px) {
      .dv-hero { gap:28px; }
      .dv-hero-right { justify-content:stretch; }
      .dv-h1 { font-size:clamp(43px,14vw,62px); }
      .dv-term-body { min-height:190px;padding:16px 14px 18px;font-size:11px;overflow:hidden; }
      .dv-term-body .dv-txt { min-width:0;white-space:normal;overflow-wrap:anywhere; }
      .dv-cta { padding:36px 20px; }
      .dv-term-title { max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
      .dv-stack { gap:6px; }
      .dv-stack-chip { padding:6px 9px;font-size:9.5px; }
      .dv-stats { grid-template-columns:1fr; }
      .dv-stat-cell { border-right:none;border-bottom:1px solid var(--line); }
      .dv-stat-cell:last-child { border-bottom:none; }
    }
    `}</style>
  );
}

/* ── page ──────────────────────────────────────────── */
export default function TeamPage() {
  const { go } = useAppStore();
  const [statRef, statSeen] = useInView(0.4);
  const titleRef = useRef(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      animate(el, {
        innerHTML: scrambleText({ chars: 'uppercase', cursor: '_' }),
        duration: 1500,
        ease: 'linear',
        delay: 250,
      });
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const openIg = () => window.open(ITSHECHKA_IG, '_blank', 'noopener,noreferrer');
  const scrollToTeam = () => {
    const el = document.getElementById('dv-team');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
  };

  return (
    <div className="page-in" style={{ position: 'relative', zIndex: 1 }}>
      <DevStyles />

      {/* back */}
      <div className="container" style={{ paddingTop: 22 }}>
        <button onClick={() => go('home')} className="dv-back mono">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          НА ГЛАВНУЮ
        </button>
      </div>

      {/* ── HERO ── */}
      <section className="container" style={{ paddingTop: 24, paddingBottom: 30 }}>
        <div className="dv-hero hero-grid">
          <div className="col" style={{ gap: 24 }}>
            <Reveal>
              <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 18, height: 2, background: 'var(--accent)', flexShrink: 0 }} />
                СТУДИЯ — РАЗРАБОТЧИКИ ЭТОГО САЙТА
              </div>
            </Reveal>

            <Reveal delay={70}>
              <h1 className="display dv-h1" style={{ margin: 0 }}>
                <span ref={titleRef} className="dv-h1-shimmer">ITSHECHKA</span><br />
                <span className="dv-h1-stroke">DEV&nbsp;STUDIO</span>
              </h1>
            </Reveal>

            <Reveal delay={140}>
              <p className="dv-lead">
                Более <strong>2 лет</strong> создаём цифровые продукты. Более <strong>10 успешных проектов</strong> для бизнеса —
                автоматизация, AI-интеграции и веб-платформы любой сложности. Этот турнирный сайт — одна из наших работ.
              </p>
            </Reveal>

            <Reveal delay={210}>
              <div className="row gap-3 wrap">
                <Btn size="lg" variant="gold" onClick={openIg} icon={<IgIcon size={18} />}>Связаться с нами</Btn>
                <Btn size="lg" variant="outline" onClick={scrollToTeam}>Наша команда</Btn>
              </div>
            </Reveal>

            <Reveal delay={280}>
              <div className="dv-stack">
                {TECH_STACK.map((t, i) => (
                  <span key={t} className="mono dv-stack-chip" style={{ transitionDelay: `${i * 40}ms` }}>{t}</span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={160} className="dv-hero-right">
            <DevTerminal />
          </Reveal>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="container" style={{ marginBottom: 64 }}>
        <Reveal>
          <div ref={statRef}>
            <Panel pad={0} glow style={{ overflow: 'hidden' }}>
              <div className="dv-stats">
                {TEAM_STATS.map((s, i) => (
                  <div key={i} className="dv-stat-cell">
                    <div className="display dv-stat-num" style={{ color: s.inf || i === 1 ? 'var(--gold)' : 'var(--fg)' }}>
                      <StatNum num={s.num} suffix={s.suffix} inf={s.inf} seen={statSeen} dur={1300 + i * 200} />
                    </div>
                    <div className="mono dv-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </Reveal>
      </section>

      {/* ── MARQUEE ── */}
      <section style={{ marginBottom: 64, overflow: 'hidden', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)', padding: '16px 0', background: 'rgba(255,255,255,0.012)' }}>
        <div className="dv-marquee" style={{ animationDuration: '34s' }}>
          {[0, 1].map(k => (
            <span key={k} className="dv-marquee-inner">
              {['РАЗРАБОТКА', 'АВТОМАТИЗАЦИЯ', 'AI · НЕЙРОСЕТИ', 'ВЕБ-ПЛАТФОРМЫ', 'API', 'БОТЫ', 'CRM', 'DEPLOY'].map((w, i) => (
                <span key={i} className="dv-marquee-item">
                  <span className="display">{w}</span>
                  <Crosshair style={{ width: 16, opacity: 0.5 }} color="var(--accent)" />
                </span>
              ))}
            </span>
          ))}
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="container" style={{ marginBottom: 76 }}>
        <Reveal>
          <SectionTitle center eyebrow="Что мы делаем" title="Направления работы"
            sub="Закрываем полный цикл: от идеи и проектирования до деплоя и поддержки." />
        </Reveal>
        <div className="dv-svc-grid">
          {TEAM_SERVICES.map((s, i) => (
            <Reveal key={i} delay={i * 90}><DevServiceCard {...s} index={i} /></Reveal>
          ))}
        </div>
      </section>

      {/* ── TEAM ── */}
      <section id="dv-team" className="container" style={{ marginBottom: 84 }}>
        <Reveal>
          <SectionTitle center eyebrow="Состав" title="Наши разработчики"
            sub="Маленькая команда с большой отдачей. Каждый отвечает за свой фронт работ." />
        </Reveal>
        <div className="dv-mem-grid">
          {TEAM_MEMBERS.map((m, i) => (
            <Reveal key={m.name} delay={i * 110} y={28}><DevMemberCard m={m} /></Reveal>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="container" style={{ marginBottom: 64 }}>
        <Reveal>
          <div className="grain dv-cta">
            <div aria-hidden className="dv-cta-glow" />
            <div style={{ position: 'relative' }}>
              <div className="eyebrow" style={{ color: 'var(--gold)', display: 'flex', justifyContent: 'center' }}>Есть задача?</div>
              <h2 className="display dv-cta-title">Напишите нам. Обсудим.</h2>
              <p className="dv-cta-sub">
                Автоматизация, веб-сайт, AI-интеграция или нестандартная задача — расскажите о проекте, и мы предложим решение.
              </p>
              <Btn size="lg" variant="gold" onClick={openIg} icon={<IgIcon size={18} />}>Instagram — написать команде</Btn>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
